import React, { useState } from "react";
import useBackdropClose from "../../hooks/useBackdropClose";
import { useTranslation } from "react-i18next";
import { pathologyName } from "../../config/pathologies";
import {
  Overlay, Modal, Header, ModalTitle, CloseButton,
  Columns, Group, GroupTitle,
  CheckRow, Checkbox, CheckLabel,
  AgeRow, Field, Label, Input,
  ActionRow, RightActions, ResetButton, CancelButton, SubmitButton,
} from "./styles";

const toggle = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const PatientFilterModal = ({
  filters, defaultFilters, therapists, pathologies, cohorts, sexes,
  currentUserId, onApply, onClose,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(filters || defaultFilters);

  const toggleIn = (key, value) =>
    setDraft((prev) => ({ ...prev, [key]: toggle(prev[key], value) }));

  const setValue = (key) => (e) =>
    setDraft((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <Overlay {...useBackdropClose(onClose)}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <ModalTitle>{t("filter.title")}</ModalTitle>
          <CloseButton onClick={onClose} aria-label={t("filter.cancel")}>✕</CloseButton>
        </Header>

        <Columns>
          <Group>
            <GroupTitle>{t("filter.therapist")}</GroupTitle>
            {therapists.map((th) => (
              <CheckRow key={th.id}>
                <Checkbox
                  type="checkbox"
                  id={`f-th-${th.id}`}
                  checked={draft.therapistIds.includes(th.id)}
                  onChange={() => toggleIn("therapistIds", th.id)}
                />
                <CheckLabel htmlFor={`f-th-${th.id}`}>
                  {th.display_name || th.username}
                  {th.id === currentUserId ? ` ${t("filter.me")}` : ""}
                </CheckLabel>
              </CheckRow>
            ))}
          </Group>

          <Group>
            <GroupTitle>{t("filter.cohort")}</GroupTitle>
            {cohorts.map((c) => (
              <CheckRow key={c}>
                <Checkbox
                  type="checkbox"
                  id={`f-co-${c}`}
                  checked={draft.cohorts.includes(c)}
                  onChange={() => toggleIn("cohorts", c)}
                />
                <CheckLabel htmlFor={`f-co-${c}`}>{c}</CheckLabel>
              </CheckRow>
            ))}
          </Group>

          <Group>
            <GroupTitle>{t("filter.sex")}</GroupTitle>
            {sexes.map((s) => (
              <CheckRow key={s}>
                <Checkbox
                  type="checkbox"
                  id={`f-sx-${s}`}
                  checked={draft.sexes.includes(s)}
                  onChange={() => toggleIn("sexes", s)}
                />
                <CheckLabel htmlFor={`f-sx-${s}`}>{s}</CheckLabel>
              </CheckRow>
            ))}
          </Group>
        </Columns>

        <Group>
          <GroupTitle>{t("filter.age")}</GroupTitle>
          <AgeRow>
            <Field>
              <Label htmlFor="f-age-min">{t("filter.ageMin")}</Label>
              <Input
                id="f-age-min"
                type="number"
                min="0"
                placeholder={t("filter.ageMinPlaceholder")}
                value={draft.ageMin}
                onChange={setValue("ageMin")}
              />
            </Field>
            <Field>
              <Label htmlFor="f-age-max">{t("filter.ageMax")}</Label>
              <Input
                id="f-age-max"
                type="number"
                min="0"
                placeholder={t("filter.ageMaxPlaceholder")}
                value={draft.ageMax}
                onChange={setValue("ageMax")}
              />
            </Field>
          </AgeRow>
        </Group>

        <Group>
          <GroupTitle>{t("filter.primaryDiagnosis")}</GroupTitle>
          {pathologies.map((p) => (
            <CheckRow key={p.id}>
              <Checkbox
                type="checkbox"
                id={`f-pa-${p.id}`}
                checked={draft.pathologyIds.includes(p.id)}
                onChange={() => toggleIn("pathologyIds", p.id)}
              />
              <CheckLabel htmlFor={`f-pa-${p.id}`}>{pathologyName(t, p.id, p.name)}</CheckLabel>
            </CheckRow>
          ))}
        </Group>

        <ActionRow>
          <ResetButton onClick={() => setDraft(defaultFilters)}>
            {t("filter.reset")}
          </ResetButton>
          <RightActions>
            <CancelButton onClick={onClose}>{t("filter.cancel")}</CancelButton>
            <SubmitButton onClick={() => onApply(draft)}>{t("filter.apply")}</SubmitButton>
          </RightActions>
        </ActionRow>
      </Modal>
    </Overlay>
  );
};

export default PatientFilterModal;
